import { Text, Line, Circle, Ring } from '@react-three/drei'
import { Group, Matrix4, Object3DEventMap, Vector2, Vector3 } from 'three'
import { useAppDispatch, useAppSelector } from '~/store/hooks'
import Units from '../../lib/units'
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { updateColumnLine, updateMountingBeam } from '~/store/objects'
import {
  become3,
  getLineSegmentForPoint,
  getOrthoOrientation,
  inchesToNative2,
  useMountingStructureData,
  useRoofSectionOrientation,
} from '../util'
import { MountingStructureLineButtons } from './Buttons'
import { MountingStructureLineMeasures } from './Measures'
import { DragControls } from '../../util/DragControls'
import LayerKeys from '~/config/layerKeys'
import { selectObjects } from '~/store/selectedObjects'
import ObjectClassName from '~/config/objectClassNames'
import { handleClick } from '~/components/DrawingCanvas/util/eventHandlers'

export function MountingStructureLine() {
  const structureData = useMountingStructureData()
  const parentGroup = useRef<Group<Object3DEventMap>>(null)
  const matrixRef = useRef(new Matrix4())
  const dragPreviewUpdateRef = useRef<() => void>(null)
  const dispatch = useAppDispatch()
  const is3D = useAppSelector(store => store.camera.is3D)
  const [isDragging, setDragging] = useState(false)
  useEffect(() => {
    matrixRef.current.identity()
  }, [structureData.model])

  const textZ = is3D ? 3 : 522
  const circleZ = is3D ? 2 : 518

  const orthogonal = getOrthoOrientation(structureData.orientation)

  const thisModel = getLineSegmentForPoint(
    structureData.model,
    structureData.perimeterPoints,
    structureData.orientation,
    false
  )
  const nextModel =
    structureData.next &&
    getLineSegmentForPoint(structureData.next, structureData.perimeterPoints, structureData.orientation, false)

  if (thisModel === undefined) {
    return <></>
  }

  const isSelected = structureData.selected

  return (
    <group ref={parentGroup}>
      <DragControls
        onDragStart={() => {
          if (!isSelected) return
          setDragging(true)
        }}
        onDrag={(_, delta) => {
          if (!isSelected) {
            return
          }
          delta.projectOnVector(orthogonal)
          matrixRef.current.makeTranslation(delta)
          dragPreviewUpdateRef.current?.()
        }}
        onDragEnd={() => {
          setDragging(false)
          if (!isSelected) {
            return
          }
          const deltaVector = new Vector3()
          deltaVector.setFromMatrixPosition(matrixRef.current)
          const newPosition = Units.nativeToUnitsV('inches', deltaVector).add(thisModel.position)
          if (structureData.type === 'beam') {
            dispatch(
              updateMountingBeam({
                roofSectionId: structureData.roofSectionId,
                beamId: thisModel.id,
                beamPosition: { x: newPosition.x, y: newPosition.y, z: newPosition.z },
                direction: { x: 0, y: 0, z: 0 },
              })
            )
          } else if (structureData.type === 'column') {
            dispatch(
              updateColumnLine({
                roofSectionId: structureData.roofSectionId,
                columnLineId: thisModel.id,
                columnLinePosition: { x: newPosition.x, y: newPosition.y, z: newPosition.z },
                direction: { x: 0, y: 0, z: 0 },
              })
            )
          } else {
            structureData.type satisfies never
            throw new Error('unreachable: mounting line is neither column nor beam')
          }
        }}
      >
        <group visible={!isDragging}>
          <MountingStructureLineLine model={structureData.model} />
        </group>
      </DragControls>
      {isDragging && <MountingStructureLineDragPreview ref={dragPreviewUpdateRef} matrix={matrixRef} />}
      {isSelected && !isDragging && (
        <MountingStructureLineButtons
          thisModel={thisModel}
          nextModel={nextModel}
          roofSectionId={structureData.roofSectionId}
          type={structureData.type}
          color={structureData.color}
          textZ={textZ}
          circleZ={circleZ}
          orthogonal={orthogonal}
        />
      )}
      {isSelected && !isDragging && structureData.next && <MountingStructureLineMeasures other={structureData.next} />}
      {isSelected && !isDragging && structureData.prev && <MountingStructureLineMeasures other={structureData.prev} />}
    </group>
  )
}
const useManualRerender = (ref: React.ForwardedRef<() => void>) => {
  const [_, setState] = useState(0)
  useImperativeHandle(
    ref,
    () => () => {
      setState(it => it + 1)
    },
    []
  )
}

const MountingStructureLineDragPreview = forwardRef(function MountingStructureLineDragPreview(
  props: { matrix: React.MutableRefObject<Matrix4> },
  ref: React.ForwardedRef<() => void>
) {
  useManualRerender(ref)
  const data = useMountingStructureData()

  const newModel = Units.nativeToUnitsV(
    'inches',
    Units.unitsToNativeV('inches', data.model[0]).applyMatrix4(props.matrix.current)
  )

  return <MountingStructureLineLine model={[newModel, data.model[1]]} />
})

function MountingStructureLineLine(props: { model: [Vector3, string] }) {
  const dispatch = useAppDispatch()
  const data = useMountingStructureData()
  const is3D = useAppSelector(store => store.camera.is3D)
  const roofOrientation = useRoofSectionOrientation()

  const textZ = is3D ? 3 : 522
  const borderZ = is3D ? 3 : 520
  const circleZ = is3D ? 2 : 518
  const lineZ = is3D ? 1 : 516

  const thisModel = getLineSegmentForPoint(props.model, data.perimeterPoints, data.orientation, false)

  if (thisModel === undefined) {
    return <></>
  }

  const extensionMultiplier = roofOrientation === 'horizontal' ? -1 : 1
  const orientation2 = new Vector2(data.orientation.x, data.orientation.y)
  const makeP1 = (it: Vector2) =>
    it.clone().add(
      orientation2
        .clone()
        .setLength(data.type === 'column' ? -100 : 100)
        .multiplyScalar(extensionMultiplier)
    )
  const makeP2 = (it: Vector2) =>
    it.clone().add(
      orientation2
        .clone()
        .setLength(data.type === 'column' ? -100 : 100)
        .multiplyScalar(-1 * extensionMultiplier)
    )

  const p1 = makeP1(thisModel.point1)
  const p2 = makeP2(thisModel.point2)

  return (
    <group
      name={LayerKeys.ROOF_SECTIONS}
      onClick={handleClick(() => {
        data.setSelected([data.roofSectionId, thisModel.id])
        dispatch(selectObjects({ objects: [{ className: ObjectClassName.ROOF_SECTION, id: data.roofSectionId }] }))
      })}
    >
      <Text fontSize={6} position={become3(inchesToNative2(p1), textZ)} color={data.selected ? 'black' : '#777'}>
        {data.name}
      </Text>
      <Ring
        args={[4, 4.5]}
        position={become3(inchesToNative2(p1), borderZ)}
        material-color={data.selected ? data.color : '#777'}
      />
      <Circle
        args={[4.5, 20]}
        position={become3(inchesToNative2(p1), circleZ)}
        material-color={data.selected ? data.color : '#fff'}
        material-toneMapped={data.selected}
      />
      <Text fontSize={6} position={become3(inchesToNative2(p2), textZ)} color={data.selected ? 'black' : '#777'}>
        {data.name}
      </Text>
      <Ring
        args={[4, 4.5]}
        position={become3(inchesToNative2(p2), borderZ)}
        material-color={data.selected ? data.color : '#777'}
      />
      <Circle
        args={[4.5, 20]}
        position={become3(inchesToNative2(p2), circleZ)}
        material-color={data.selected ? data.color : '#fff'}
        material-toneMapped={data.selected}
      />
      <Line
        points={[become3(inchesToNative2(p1), lineZ), become3(inchesToNative2(p2), lineZ)]}
        lineWidth={2}
        dashed={true}
        color={data.selected ? data.color : '#777'}
      />
    </group>
  )
}
